import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';
import { useLoader } from '../context/loaders/useLoader';
import { useToast } from '../context/toasts/useToast';
import type { Wiki, CreateWikiRequest, UpdateWikiRequest } from '../types/epic';

function useWikis(boardId: string) {
  const [wikis, setWikis] = useState<Wiki[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const {
    getWikis,
    createWiki: apiCreateWiki,
    updateWiki: apiUpdateWiki,
    deleteWiki: apiDeleteWiki,
  } = useApi();
  const { withLoader } = useLoader();
  const { addNotification } = useToast();

  const fetchWikis = useCallback(async () => {
    try {
      setError(null);
      const data = await withLoader(() => getWikis(Number(boardId)), 'Loading wikis...');
      setWikis(data);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load wikis';
      setError(err instanceof Error ? err : new Error(errorMessage));
      addNotification({
        type: 'error',
        title: 'Error Loading Wikis',
        message: errorMessage,
        duration: 5000,
      });
      return [];
    }
  }, [boardId, getWikis, withLoader, addNotification]);

  const createWiki = useCallback(
    async (wikiData: CreateWikiRequest) => {
      try {
        setError(null);
        const newWiki = await withLoader(
          () => apiCreateWiki(Number(boardId), wikiData),
          'Creating wiki...'
        );
        setWikis((prev) => [...prev, newWiki]);
        addNotification({
          type: 'success',
          title: 'Wiki Created',
          message: `Wiki "${wikiData.name}" created successfully.`,
          duration: 3000,
        });
        return newWiki;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create wiki';
        setError(err instanceof Error ? err : new Error(errorMessage));
        addNotification({
          type: 'error',
          title: 'Error Creating Wiki',
          message: errorMessage,
          duration: 5000,
        });
        throw err;
      }
    },
    [boardId, apiCreateWiki, withLoader, addNotification]
  );

  const updateWiki = useCallback(
    async (wikiId: number, wikiData: UpdateWikiRequest) => {
      try {
        setError(null);
        const updatedWiki = await withLoader(
          () => apiUpdateWiki(wikiId, wikiData),
          'Updating wiki...'
        );
        setWikis((prev) => prev.map((wiki) => (wiki.id === wikiId ? updatedWiki : wiki)));
        addNotification({
          type: 'success',
          title: 'Wiki Updated',
          message: 'Wiki updated successfully.',
          duration: 3000,
        });
        return updatedWiki;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update wiki';
        setError(err instanceof Error ? err : new Error(errorMessage));
        addNotification({
          type: 'error',
          title: 'Error Updating Wiki',
          message: errorMessage,
          duration: 5000,
        });
        throw err;
      }
    },
    [apiUpdateWiki, withLoader, addNotification]
  );

  const deleteWiki = useCallback(
    async (wikiId: number) => {
      try {
        setError(null);
        await withLoader(() => apiDeleteWiki(wikiId), 'Deleting wiki...');
        setWikis((prev) => prev.filter((wiki) => wiki.id !== wikiId));
        addNotification({
          type: 'success',
          title: 'Wiki Deleted',
          message: 'Wiki deleted successfully.',
          duration: 3000,
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete wiki';
        setError(err instanceof Error ? err : new Error(errorMessage));
        addNotification({
          type: 'error',
          title: 'Error Deleting Wiki',
          message: errorMessage,
          duration: 5000,
        });
        throw err;
      }
    },
    [apiDeleteWiki, withLoader, addNotification]
  );

  useEffect(() => {
    fetchWikis();
  }, [fetchWikis]);

  return {
    wikis,
    error, // For debugging, not for UI display
    loading: false, // Loading is handled by global loader
    fetchWikis,
    createWiki,
    updateWiki,
    deleteWiki,
    refetch: fetchWikis,
  };
}

export default useWikis;
